import type { CSSProperties } from 'react';
import { useGetPaymentControlPlaneReadinessQuery } from '../../../state/domainRouterApi';

type TuitionPaymentsReadinessCalloutProps = {
  testId: string;
  roleLabel: string;
};

const statusToneClass = (status: string): string => {
  switch (status) {
    case 'live_ready':
      return 'border-[#0F766E] bg-[#ECFDF5] text-[#065F46]';
    case 'test_ready':
      return 'border-[#B45309] bg-[#FFFBEB] text-[#92400E]';
    default:
      return 'border-[#DC2626] bg-[#FEF2F2] text-[#991B1B]';
  }
};

const formatStatus = (status: string): string => {
  if (status === 'live_ready') return 'Live-ready';
  if (status === 'test_ready') return 'Test-ready';
  return 'Not ready';
};

export function TuitionPaymentsReadinessCallout({
  testId,
  roleLabel,
}: TuitionPaymentsReadinessCalloutProps) {
  const readinessQuery = useGetPaymentControlPlaneReadinessQuery({});
  const readiness = readinessQuery.data ?? null;

  if (readinessQuery.isLoading) {
    return (
      <div
        data-testid={testId}
        className="rounded-xl border border-border bg-surface-light px-6 py-4 text-sm text-text-secondary"
      >
        Loading payment readiness…
      </div>
    );
  }

  if (!readiness) {
    return (
      <div
        data-testid={testId}
        className="rounded-xl border border-amber-200 bg-amber-50 px-6 py-4 text-sm text-amber-800"
      >
        Payment readiness is unavailable. Recheck school settings before relying on hosted checkout.
      </div>
    );
  }

  return (
    <section
      data-testid={testId}
      className={`rounded-xl border px-6 py-5 ${statusToneClass(String(readiness.status ?? 'not_ready'))}`}
      style={{ '--ystnj-source-faithful-max-width': '1120px' } as CSSProperties}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">Payments readiness</p>
          <h2 className="mt-2 text-[16px] font-semibold">
            {roleLabel} payment handoff depends on school settings readiness
          </h2>
          <p className="mt-1 text-sm opacity-90">
            Status {formatStatus(String(readiness.status ?? 'not_ready'))} · onboarding {String(readiness.controlPlane?.onboardingStatus ?? 'unknown')} · default account {String(readiness.controlPlane?.defaultStripeAccountId ?? 'not configured')}
          </p>
        </div>
        <a
          href="/school/settings/general"
          className="inline-flex items-center rounded-full border border-current/20 bg-white/70 px-3 py-1.5 text-[12px] font-semibold"
          data-testid={`${testId}-open-settings`}
        >
          Open school settings
        </a>
      </div>
    </section>
  );
}
